import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { API_BASE_URL } from "@/api/helpers";
import { log } from "@/core/logger";
import { useQuery } from "@tanstack/react-query";
import type { Room } from "@/types/Room";

interface RoomResponse {
  room: Room;
}

/**
 * Hook to fetch a single room by ID
 */
export function useRoomQuery(roomId: string | undefined) {
  return useQuery({
    queryKey: ["room", roomId],
    queryFn: async (): Promise<Room> => {
      if (!roomId) {
        throw new Error("Room ID is required");
      }

      try {
        const response = await fetchWithAuth(`${API_BASE_URL}/api/rooms/${roomId}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || errorData.error || "Failed to fetch room");
        }

        const data: RoomResponse = await response.json();
        return data.room;
      } catch (error) {
        log("useRoomQuery", "Failed to fetch room", error);
        throw error;
      }
    },
    enabled: !!roomId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
